import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Link,
  Tooltip,
  CircularProgress
} from '@mui/material'
import {
  Dashboard as DashboardIcon,
  Refresh,
  DarkMode,
  LightMode,
  Timeline
} from '@mui/icons-material'
import config from '../config'
import UserProfile from './auth/UserProfile'

interface StandardAppBarProps {
  darkMode: boolean
  toggleDarkMode: () => void
  onRefresh?: () => void
  isLoading?: boolean
  showSearch?: boolean
  searchComponent?: React.ReactNode
}

const StandardAppBar: React.FC<StandardAppBarProps> = ({
  darkMode,
  toggleDarkMode,
  onRefresh,
  isLoading = false,
  showSearch = false,
  searchComponent
}) => {
  const navigate = useNavigate()

  return (
    <AppBar position="static">
      <Toolbar
        variant="dense"
        sx={{
          minHeight: 48,
          py: 0.5,
          px: 2
        }}
      >
        {/* Left side: Icon and Title */}
        <DashboardIcon sx={{ mr: 1.5, fontSize: 20 }} />
        <Link
          onClick={() => navigate('/')}
          sx={{
            mr: 2,
            fontSize: '1rem',
            fontWeight: 600,
            lineHeight: 1.2,
            color: 'inherit',
            textDecoration: 'none',
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8
            }
          }}
        >
          {config.app.name}
        </Link>

        {/* Optional Search Component (Dashboard only) */}
        {showSearch && searchComponent}

        {/* Right side: Navigation buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: showSearch ? 'auto' : 'auto' }}>
          {/* 1. Releases button (first) */}
          <Button
            color="inherit"
            href="/releases"
            startIcon={<Timeline sx={{ fontSize: 18 }} />}
            sx={{
              px: 1,
              py: 0.5,
              fontSize: '0.875rem',
              height: 36
            }}
          >
            Releases
          </Button>

          {/* 2. Dark Mode toggle (second) */}
          <Tooltip title={`Switch to ${darkMode ? 'light' : 'dark'} mode (Ctrl+Shift+D)`}>
            <Button
              color="inherit"
              onClick={toggleDarkMode}
              startIcon={darkMode ? <LightMode sx={{ fontSize: 18 }} /> : <DarkMode sx={{ fontSize: 18 }} />}
              sx={{
                minWidth: 'auto',
                px: 1,
                py: 0.5,
                fontSize: '0.875rem',
                height: 36
              }}
            >
              {darkMode ? 'Light' : 'Dark'}
            </Button>
          </Tooltip>

          {/* 3. Refresh button (third) */}
          {onRefresh && (
            <Button
              color="inherit"
              onClick={onRefresh}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <Refresh sx={{ fontSize: 18 }} />}
              sx={{
                px: 1,
                py: 0.5,
                fontSize: '0.875rem',
                height: 36
              }}
            >
              Refresh
            </Button>
          )}

          {/* User Profile */}
          <UserProfile />
        </Box>
      </Toolbar>
    </AppBar>
  )
}

export default StandardAppBar

